import { useState, useEffect, useCallback, useRef } from 'react';
import AccountLayout from '@/components/feature/AccountLayout';
import { fetchMyContracts, fetchDownloadProjectContract } from '@/service/api/project';

function formatFileSize(bytes?: number | null) {
  if (!bytes || bytes <= 0) return '未知大小';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr?: string) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr.substring(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getContractStatusConfig(status?: string) {
  const s = (status || '').toUpperCase();
  if (s === 'ARCHIVED' || s === 'EXECUTED' || s === 'FULLY_EXECUTED' || status === '已归档') {
    return { label: '已归档', color: 'bg-primary-100 text-primary-700' };
  }
  if (s === 'OWNER_SIGNED' || s === 'PLATFORM_SIGNED' || s === 'SIGNED' || status === '已签章') {
    return { label: '已签章', color: 'bg-accent-100 text-accent-700' };
  }
  if (s === 'DRAFT' || s === 'PENDING' || s === 'WAITING' || status === '待签章') {
    return { label: '待签章', color: 'bg-amber-100 text-amber-700' };
  }
  return { label: status || '处理中', color: 'bg-foreground-100 text-foreground-600' };
}

export default function ContractsPage() {
  const [contracts, setContracts] = useState<Api.Project.ContractListItemResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const hasFetchedRef = useRef(false);

  const loadContracts = useCallback(async (force = false) => {
    if (hasFetchedRef.current && !force) return;
    hasFetchedRef.current = true;

    setIsLoading(true);
    setErrorMessage('');
    try {
      const res = await fetchMyContracts();
      setContracts(res?.items || []);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : '获取合同列表失败');
      hasFetchedRef.current = false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  const handleDownload = async (item: Api.Project.ContractListItemResponse) => {
    const contract = item.contract;
    const targetAsset = contract?.fully_executed_asset || contract?.owner_signed_asset || contract?.platform_draft_asset;
    
    if (targetAsset?.access_url) {
      window.open(targetAsset.access_url, '_blank');
      return;
    }

    if (!item.project_id) {
      alert('暂无可下载的合同文件');
      return;
    }

    const downloadKey = item.contract?.id || item.project_id;
    setDownloadingId(downloadKey);
    try {
      const downloadRes = await fetchDownloadProjectContract(item.project_id);
      const url = downloadRes?.fully_executed_asset?.access_url || 
                  downloadRes?.owner_signed_asset?.access_url || 
                  downloadRes?.platform_draft_asset?.access_url;
      if (url) {
        window.open(url, '_blank');
      } else {
        alert('暂无可下载的合同文件');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : '下载合同文件失败');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <AccountLayout
      activePath="/contracts"
      breadcrumbs={[{ label: '账号中心', href: '/profile' }, { label: '合同文件' }]}
    >
      <div className="glass-card rounded-2xl p-6 md:p-8">
        <h1 className="font-heading font-bold text-lg text-foreground-950 mb-2">合同文件</h1>
        <p className="text-xs text-foreground-500 mb-8">查看和管理所有发行项目的合同文件</p>

        {isLoading ? (
          <div className="py-16 text-center text-foreground-400 text-sm">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent mb-3"></div>
            <p>加载合同列表中...</p>
          </div>
        ) : errorMessage ? (
          <div className="py-12 text-center">
            <i className="ri-error-warning-line text-3xl text-red-500 mb-2 block"></i>
            <p className="text-sm text-foreground-600 mb-4">{errorMessage}</p>
            <button
              onClick={() => loadContracts(true)}
              className="px-4 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 transition-all cursor-pointer"
            >
              重新加载
            </button>
          </div>
        ) : contracts.length === 0 ? (
          <div className="py-16 text-center text-foreground-400">
            <i className="ri-file-text-line text-4xl text-foreground-300 mb-2 block"></i>
            <p className="text-sm font-medium text-foreground-600">暂无合同文件</p>
            <p className="text-xs text-foreground-400 mt-1">项目签约后即可在此处查看对应的合同协议</p>
          </div>
        ) : (
          <div className="space-y-3">
            {contracts.map((item, index) => {
              const contract = item.contract;
              const contractId = contract?.id || `contract-${index}`;
              const title = item.project_title ? `《${item.project_title}》发行合作协议` : (contract?.contract_version ? `合同文件 (${contract.contract_version})` : '发行合作协议');
              const projectNo = item.project_no || item.project_id || '-';
              
              const targetAsset = contract?.fully_executed_asset || contract?.owner_signed_asset || contract?.platform_draft_asset;
              const fileSizeStr = formatFileSize(targetAsset?.file_size);
              const statusConfig = getContractStatusConfig(contract?.status || item.project_status);
              const isDownloading = downloadingId === (contract?.id || item.project_id);

              return (
                <div key={contractId} className="p-5 rounded-xl bg-gray-50 border border-gray-200/60 hover:border-gray-300 transition-all">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                        <i className="ri-file-pdf-line text-primary-600 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-950">{title}</p>
                        <p className="text-xs text-foreground-400">
                          项目 {projectNo} · {fileSizeStr}
                          {contract?.updated_at && ` · 更新于 ${formatDate(contract.updated_at)}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 self-end sm:self-center">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                      <button
                        onClick={() => handleDownload(item)}
                        disabled={isDownloading}
                        className="px-3 py-1.5 rounded-lg border border-background-300/60 text-xs text-foreground-600 hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {isDownloading ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>
                            下载中...
                          </>
                        ) : (
                          '下载'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AccountLayout>
  );
}